﻿import type { HTMLAttributes } from "react";
import { useNavigate } from "react-router-dom";
import { MIDDLE_MOUSE_BUTTON } from "~components/DataTable/constants/rowNavigation.ts";
import type { RowNavigationConfig } from "~components/DataTable/types/rowNavigationConfig.ts";

type Params<T> = {
  onRowClick?: (record: T) => HTMLAttributes<any>;
  rowNavigation?: RowNavigationConfig<T>;
  massEditMode?: boolean;
};

const hasTextSelection = () => !!window.getSelection()?.toString();

export const useRowNavigation = <T>({ onRowClick, rowNavigation, massEditMode }: Params<T>) => {
  const navigate = useNavigate();

  const buildNavigationProps =
    (config: RowNavigationConfig<T>) =>
    (record: T): HTMLAttributes<HTMLElement> => {
      const url = config.getNavigateUrl(record);
      const openInNewTab = () => window.open(url, "_blank", "noopener");

      return {
        onClick: (event) => {
          if (hasTextSelection()) {
            return;
          }
          if (event.ctrlKey || event.metaKey) {
            openInNewTab();
            return;
          }
          navigate(url);
        },
        // Middle click opens a new tab, mirroring how the browser treats a link
        onAuxClick: (event) => {
          if (event.button !== MIDDLE_MOUSE_BUTTON) {
            return;
          }
          event.preventDefault();
          openInNewTab();
        },
        onMouseDown: (event) => {
          // Suppresses the autoscroll cursor the browser would otherwise start on middle click
          if (event.button === MIDDLE_MOUSE_BUTTON) {
            event.preventDefault();
          }
        },
      };
    };

  // Rows must stay put while their cells are being edited
  const getNavigationProps = rowNavigation && !massEditMode ? buildNavigationProps(rowNavigation) : undefined;
  const getRowProps = onRowClick ?? getNavigationProps;

  return {
    getRowProps,
    getRowClassName: getRowProps ? (record: T) => (getRowProps(record).onClick ? "is-clickable" : "") : undefined,
  };
};
